import React from 'react';
import { Link, PrismicImageAdapter, Text } from '@belong/ui';
import { PrismicRichText } from '@prismicio/react';
import { kebabCase } from 'es-toolkit/compat';
import { TextContentItem } from 'src/features/cms/prismic';
import { linkResolver } from 'src/prismicio';
import { YoutubeEmbed } from '../youtube-embed/youtube-embed';

// TODO: Maybe move to common?
function extractText(children: React.ReactNode): string {
  if (typeof children === 'string' || typeof children === 'number') {
    return children.toString();
  } else if (Array.isArray(children)) {
    return children.map(extractText).join('');
  } else if (React.isValidElement(children)) {
    return extractText(children.props.children);
  }
  return '';
}

type RichTextSliceProps = {
  primary: { text: TextContentItem[] };
};

export function RichTextSlice({ primary }: RichTextSliceProps): JSX.Element {
  if (!primary) return <></>;

  return (
    <div className="w-full">
      <PrismicRichText
        // @ts-expect-error -- Legacy typecheck baseline; see docs/development/typecheck-baseline-followups.md. Refs: ARCH-56
        field={primary.text}
        components={{
          heading1: ({ children }) => {
            return (
              <Text as="h1" variant="h1" className="mb-2sm">
                {children}
              </Text>
            );
          },
          heading2: ({ children }) => {
            const idValue = extractText(children);
            return (
              <Text
                id={kebabCase(idValue ?? '')}
                as="h2"
                variant="h2"
                className="mb-xs"
              >
                {children}
              </Text>
            );
          },
          heading3: ({ children }) => {
            const idValue = extractText(children);
            return (
              <Text
                id={kebabCase(idValue ?? '')}
                as="h3"
                variant="h3"
                className="mb-2xs"
              >
                {children}
              </Text>
            );
          },
          heading4: ({ children }) => {
            return (
              <Text as="h4" variant="body">
                {children}
              </Text>
            );
          },
          heading5: ({ children }) => {
            return (
              <Text as="h5" variant="p1">
                {children}
              </Text>
            );
          },
          heading6: ({ children }) => {
            return (
              <Text as="h6" variant="p2">
                {children}
              </Text>
            );
          },
          paragraph: ({ children, text }) => {
            if (text === '') {
              return <br />;
            }

            return (
              <Text as="p" variant="body">
                {children}
              </Text>
            );
          },
          list: ({ children }) => (
            <ul className="pl-xl list-disc">{children}</ul>
          ),
          listItem: ({ children }) => <Text as="li">{children}</Text>,
          oList: ({ children }) => <ol className="pl-xl">{children}</ol>,
          oListItem: ({ children }) => <Text as="li">{children}</Text>,
          image: ({ node }) => {
            if (!node.url) {
              return null;
            }

            return (
              <div className="py-xl w-full">
                <PrismicImageAdapter src={node.url} alt={node.alt ?? ''} />
              </div>
            );
          },
          embed: ({ node }) => (
            // @ts-expect-error -- Legacy typecheck baseline; see docs/development/typecheck-baseline-followups.md. Refs: ARCH-56
            <YoutubeEmbed oembed={node.oembed} />
          ),
          hyperlink: ({ node, children }) => {
            const url =
              (node.data?.link_type === 'Document'
                ? linkResolver(node.data)
                : node.data?.url) || '';
            return (
              <Link href={url} className="inline normal-case underline">
                {children}
              </Link>
            );
          },
        }}
      />
    </div>
  );
}
